import { ESdChangeReason, ICommonSettings, ISapServiceBaseUrlSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import * as React from "react";
import { ReactNode } from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

interface IConfigSapServiceBaseUrlSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingSapServiceBaseUrlSetting?: ISapServiceBaseUrlSetting;
}

interface IConfigSapServiceBaseUrlSettingsProps {
  formInputName: string[];
  closeModal: () => void;
  showModal: boolean;
  editingSapServiceBaseUrlId: string | null;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigSapServiceBaseUrlSettings extends React.PureComponent<
  IConfigSapServiceBaseUrlSettingsProps,
  IConfigSapServiceBaseUrlSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigSapServiceBaseUrlSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingSapServiceBaseUrlSetting: undefined
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigSapServiceBaseUrlSettingsProps) {
    if (prevProps.editingSapServiceBaseUrlId && !this.props.editingSapServiceBaseUrlId) {
      this.formRef.current?.resetFields();
    }
    if (prevProps.editingSapServiceBaseUrlId !== this.props.editingSapServiceBaseUrlId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingSapServiceBaseUrlId) return;

    const settings = _.cloneDeep(this.props.commonSettings?.sapServiceBaseUrlSettings);
    const editingSetting = settings?.find((s) => s.id === this.props.editingSapServiceBaseUrlId);
    if (editingSetting)
      this.setState({ editingSapServiceBaseUrlSetting: editingSetting }, () => {
        this.formRef.current?.resetFields();
        this.formRef.current?.setFieldsValue(editingSetting);
      });
  };

  existingNameValidator = async (_rule: unknown, value: string) => {
    const settings = this.props.commonSettings?.sapServiceBaseUrlSettings;
    const isAlreadyExist =
      settings?.some((x) => x.name === value && x.id !== this.props.editingSapServiceBaseUrlId) || value === "Default";
    if (isAlreadyExist) throw new Error("SAP Service Base URL Setting Name already exists.");
  };

  onFinish = (data: ISapServiceBaseUrlSetting) => {
    this.saveStatus.start();
    const obj: ISapServiceBaseUrlSetting = {
      id:
        this.props.editingSapServiceBaseUrlId && this.props.editingSapServiceBaseUrlId !== ""
          ? this.props.editingSapServiceBaseUrlId
          : Guid.create().toString(),
      name: data.name,
      sapServiceBaseUrl: data.sapServiceBaseUrl
    };
    if (this.props.editingSapServiceBaseUrlId) {
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "sapServiceBaseUrlSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "sapServiceBaseUrlSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = () => {};

  deleteSetting = () => {
    if (!this.props.editingSapServiceBaseUrlId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingSapServiceBaseUrlId || "",
      configKey: "sapServiceBaseUrlSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`SAP Service Base URL setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="SAP Service Base URL Setting"
        secondHeader="SAP Service Base URL Setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingSapServiceBaseUrlId ? this.deleteSetting : undefined}
            deleteTestId="sapServiceBaseUrlSettingDeleteButton"
            submitTestId="sapServiceBaseUrlSettingCreateButton"
            submitLabel={this.props.editingSapServiceBaseUrlId ? "UPDATE" : "CREATE"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configSapServiceBaseUrlSettingsForm"
          />
        }
      >
        <Form
          id="configSapServiceBaseUrlSettingsForm"
          name="sapServiceBaseUrlSettings"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("appsettings_sapservicebaseurlsettingpop_up_description")}
          >
            <div>
              <ConfigField label="Name" required tooltipContentKey="appsettings_sapservicebaseurlsettingpop_up_name">
                <Form.Item
                  name="name"
                  rules={[
                    { required: true, message: "Please enter a SAP Service Base URL Setting Name." },
                    {
                      pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                      message: "SAP Service Base URL Setting Name is invalid."
                    },
                    { validator: this.existingNameValidator }
                  ]}
                >
                  <KuikaInput size="md" data-testid="sapServiceBaseUrlSettingNameInput" autoFocus />
                </Form.Item>
              </ConfigField>

              <ConfigField
                label="SAP Service Base URL"
                required
                tooltipContentKey="appsettings_sapservicebaseurlsettingpop_up_url"
              >
                <Form.Item
                  name="sapServiceBaseUrl"
                  rules={[
                    { whitespace: true, required: true, message: "Please enter a SAP Service Base URL." },
                    {
                      pattern:
                        /^https?:\/\/(localhost|(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,15})|(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}))(:\d{1,5})?(\/\S*)?(\?\S*)?$/,
                      message: "SAP Service Base URL is not valid."
                    }
                  ]}
                >
                  <KuikaInput size="md" data-testid="sapServiceBaseUrlSettingUrlInput" />
                </Form.Item>
              </ConfigField>
            </div>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return false;
  };
}
